import React, { useEffect, useState, useRef } from "react";
import { useForm } from "react-hook-form";
import { Input, LabelText, SubmitButton, FlexContainer, LeftBackButtonText, H1Text, Hr, Text, Img, InputWrapper } from "../../design";
import { Controller } from "react-hook-form";
import { ErrorMessage } from "@hookform/error-message";
import { API, axiosLoginInstance, BASE_URLS, getRecaptchToken } from "../../api";
import { EMAIL, TOKENS, AUTH_CHALLENGES, US_FILING_GID } from "../../appconstants";
import BaseSplitPage from "../../components/splitpage";
import Header from "./smallscreen/header";
import Support from "../../components/support";
import PaymentError from "../../components/paymenterror";
import OTP from "../../components/otp";
import ArrowIcon from "../../assets/imgs/forwardarrow.svg";
import OpenIcon from "../../assets/imgs/eyeopen.svg";
import HiddenIcon from "../../assets/imgs/eyeclose.svg";
import ReCAPTCHA from 'react-google-recaptcha';
import RecaptchaModal from "../../components/recaptcha";
import {
    getOtpAuthErrorMessage,
    getOtpChallengeDetail,
    isOtpAuthError,
} from "../../utils/authOtp";

type Props = {
    history: {
        push: Function,
    },
}

const Login = (props: Props) => {
    const [errorMessage, setErrorMessage] = useState();
    const [readOnly] = useState([]);
    const [showerrorMessage, setShowErrorMessage] = useState('');
    const [success, setSuccess] = useState(false);
    const publicKey = localStorage.getItem('publicKey');
    const [showPassword, setShowPassword] = useState(false);
    const [sitekey, setSitekey] = useState();
    const [recaptchaToken, setRecaptchToken] = useState(null);
    const [isLogin, setIsLogin] = useState(false);
    const [showCaptchError, setShowCaptchError] = useState(null);
    const [showOTP, setShowOTP] = useState(false);
    const [otpDetail, setOtpDetail] = useState({
        userEmail: '',
        session: '',
        hint: '',
        status: '',
        authSuccessCallback: () => {},
    });
    const [parentAccGid, setParentAccGid] = useState(null);

    const { handleSubmit, errors, control } = useForm();

    const authMode = localStorage.getItem('authMode');
    const savedAuthData = useRef(null);
    const [showRecaptcha, setShowRecaptcha] = useState(false);

    const showOtpChallenge = (entity, email, authSuccessCallback) => {
        setShowOTP(true);
        setOtpDetail({
            ...getOtpChallengeDetail(entity, email),
            authSuccessCallback,
        });
    };

    const handleOtpAuthError = (error) => {
        if (!isOtpAuthError(error)) {
            return false;
        }
        setShowErrorMessage(true);
        setErrorMessage(getOtpAuthErrorMessage(error));
        return true;
    };

    useEffect(() => {
        const getAccount = async () => {
            if (!publicKey) {
                props.history.push('/error');
                return;
            }
            try {
                const config = {
                    headers: {
                        'x-api-key': publicKey,
                    }
                };
                const { data } = await API.get(BASE_URLS.GET_ACCOUNT, config);
                if (data?.entity?.gid) {
                    setParentAccGid(data?.entity?.gid);
                };
            } catch (e) {
                const status = e?.status || e?.response?.status;
                const responseCode = e?.data?.responseCode || e?.response?.data?.responseCode;
                if (status === 401 || responseCode === 401) {
                    props.history.push('/error');
                } else {
                    setShowErrorMessage(true);
                    setErrorMessage(e && e.data ? e.data.message : 'Unable to process request. Please try again later');
                }
            }
        };

        const getRecaptha = async () => {
            try {
                const value = await getRecaptchToken();
                if (value) {
                    setSitekey(value.siteKey);
                    setIsLogin(value.login);
                }
            } catch (reason) {
                console.log(reason);
            }
        };

        getRecaptha();
        getAccount();
    }, [publicKey, props.history]);

    const onSubmit = async (data, localRecaptchaToken = null) => {
        if (recaptchaToken) {
            setShowCaptchError(null);
        } else {
            setShowCaptchError('Captcha is required');
        }
        // recatpcha modal for the rate limiting
        const config = {};
        if (typeof localRecaptchaToken === 'string') {
            config.headers = {
                'X-Captcha-Token': localRecaptchaToken,
            };
        }
        localStorage.setItem('email', data.email.trim());
        if (isLogin === true) {
            if (recaptchaToken || typeof localRecaptchaToken === 'string') {
                try {
                    const { data: { entity } } = await axiosLoginInstance.post(BASE_URLS.AUTHENTICATE, {
                        email: data.email.trim(),
                        password: data.password,
                        recaptchaToken,
                        platformType: 'WEB',
                    }, config);
                    const authSuccessCallback = async (authEntity) => {
                        if (authEntity.token) {
                            localStorage.setItem(TOKENS.ACCESS_TOKEN, authEntity.token.accessToken);
                            localStorage.setItem(TOKENS.REFRESH_TOKEN, authEntity.token.refreshToken);
                            setSuccess(true);
                        } else {
                            localStorage.setItem(TOKENS.SESSION_TOKEN, authEntity.session);
                            if (authEntity.challengeName === 'NEW_PASSWORD_REQUIRED') {
                                props.history.replace('/setuppassword');
                            }
                        }
                    };
                    if (entity.challengeName === AUTH_CHALLENGES.CUSTOM_CHALLENGE) {
                        showOtpChallenge(entity, data.email.trim(), authSuccessCallback);
                    } else if (entity) {
                        await authSuccessCallback(entity);
                    }
                } catch (e) {
                    if (handleOtpAuthError(e)) {
                        return;
                    }
                    if ((e?.status || e?.response?.status) === 429) {
                        savedAuthData.current = data;
                        setShowRecaptcha(true);
                        return;
                    }
                    setShowErrorMessage(true);
                    setErrorMessage(e && e.data ? e.data.message : 'Unable to process request. Please try again later');
                }
            }
        } else {
            try {
                const { data: { entity } } = await axiosLoginInstance.post(BASE_URLS.AUTHENTICATE, {
                    email: data.email.trim(),
                    password: data.password,
                }, config);
                const authSuccessCallback = async (authEntity) => {
                    if (authEntity.token) {
                        localStorage.setItem(TOKENS.ACCESS_TOKEN, authEntity.token.accessToken);
                        localStorage.setItem(TOKENS.REFRESH_TOKEN, authEntity.token.refreshToken);
                        setSuccess(true);
                    } else {
                        localStorage.setItem(TOKENS.SESSION_TOKEN, authEntity.session);
                        if (authEntity.challengeName === 'NEW_PASSWORD_REQUIRED') {
                            props.history.replace('/setuppassword');
                        }
                    }
                };
                if (entity.challengeName === AUTH_CHALLENGES.CUSTOM_CHALLENGE) {
                    showOtpChallenge(entity, data.email.trim(), authSuccessCallback);
                } else if (entity) {
                    await authSuccessCallback(entity);
                }
            } catch (e) {
                if (handleOtpAuthError(e)) {
                    return;
                }
                if ((e?.status || e?.response?.status) === 429) {
                    savedAuthData.current = data;
                    setShowRecaptcha(true);
                    return;
                }
                setShowErrorMessage(true);
                setErrorMessage(e && e.data ? e.data.message : 'Unable to process request. Please try again later');
            }
        }
    }

    useEffect(() => {
        if (success) {
            if (authMode === 'AUTH') {
                props.history.replace('/partnerlink');
                localStorage.removeItem('authMode');
            } else if (parentAccGid === US_FILING_GID) {
                props.history.replace('/us-filing-partner-business');
            } else {
                props.history.replace('/partner-business');  
            }
        }
    }, [success, authMode, parentAccGid, props.history]);

    const onGlobalRecaptchaVerified = (token) => {
        setShowRecaptcha(false);
        onSubmit(savedAuthData.current, token);
    }

    return (
        <BaseSplitPage left={
            <>
                <FlexContainer
                    flexDirection="column"
                    position="relative"
                    display="block"
                    height="100%"
                >
                    <div>
                        <LeftBackButtonText
                            onClick={() => {
                                const affiliateId = localStorage.getItem('affiliateId');
                                const redirectUrl = localStorage.getItem('redirectUrl');
                                if (affiliateId) {
                                  props.history.push(`/connect/create?key=${btoa(publicKey)}&affiliateId=${affiliateId}`);
                                } else if (redirectUrl) {
                                  props.history.push(`/connect/create?key=${btoa(publicKey)}&redirectUrl=${redirectUrl}`);
                                } else {
                                  props.history.push(`/connect/create?key=${btoa(publicKey)}`);
                                }
                            }}
                        >
                            Back
                        </LeftBackButtonText>
                        <H1Text>
                            Configure Payments
                        </H1Text>
                    </div>
                    <div style={{ position: "sticky", top: "85%" }}>
                        <Support email={null}
                            phone={null} />
                    </div>
                </FlexContainer>
            </>
        } right={
            <>
                <Header payment={null} />
                {
                    !showOTP && (
                        <FlexContainer
                            className="pt-3 pt-lg-5"
                            box-shadow="0 0 15px 0 rgba(0, 0, 0, 0.1)"
                            display="flow-root"
                        >
                            <H1Text
                                className="px-1 px-md-4 mt-4"
                            >
                                ACCOUNT SETUP
                            </H1Text>

                            <Text className="px-1 px-md-4" paddingBottom="8px">
                                Sign in to your account
                            </Text>
                            <Hr />
                            <div className="px-1 px-md-4 mt-2">
                                <FlexContainer
                                    flexDirection="column"
                                >
                                    <form onSubmit={handleSubmit(onSubmit)}>
                                        {/* register your input into the hook by invoking the "register" function */}

                                        <LabelText className="mt-4 mb-2">
                                            Email
                                        </LabelText>
                                        <Controller
                                            marginBottom="0px"
                                            defaultValue={""}
                                            control={control}
                                            name="email"
                                            placeholder="Enter your email address"
                                            rules={{
                                                required: 'Email is required',
                                                pattern: {
                                                    value: EMAIL.DEFAULT_REGEX,
                                                    message: EMAIL.DEFAULT_ERROR_MESSAGE,
                                                },
                                            }}
                                            as={<Input readOnly={readOnly && readOnly.includes("email")} />}
                                        />
                                        <ErrorMessage
                                            errors={errors}
                                            name="email"
                                            render={({ message }) => (
                                                <Text fontSize="12px" marginTop="4px" marginBottom="0px" color="#ff1e1e">{message}</Text>
                                            )}
                                        />
                                        <LabelText className="mt-4 mb-2">
                                            Password
                                        </LabelText>
                                        <InputWrapper marginBottom="0px" borderRadius="4px" width='298px'>
                                            <Controller
                                                defaultValue={""}
                                                control={control}
                                                color={errors.password ? "red" : "inherit"}
                                                border="0px !important"
                                                name="password"
                                                placeholder="Enter your Password"
                                                rules={{
                                                    required: 'Password is required'
                                                }}
                                                as={<Input style={{
                                                    marginTop: "0",
                                                    marginBottom: "0px",
                                                    width: "268px",
                                                }}
                                                    type={showPassword ? "text" : "password"} readOnly={readOnly && readOnly.includes("password")} />}
                                            />
                                            <Img verticalAlign='middle' onClick={() => setShowPassword(!showPassword)} src={showPassword ? OpenIcon : HiddenIcon} />
                                        </InputWrapper>
                                        <ErrorMessage
                                            errors={errors}
                                            name="password"
                                            render={({ message }) => (
                                                <Text fontSize="12px" marginTop="4px" marginBottom="0px" color="#ff1e1e">{message}</Text>
                                            )}
                                        />
                                        {
                                            sitekey && (isLogin === true) && (
                                                <div className="mt-2">
                                                    <ReCAPTCHA
                                                        sitekey={sitekey}
                                                        value={recaptchaToken}
                                                        onChange={(value) => {
                                                            setRecaptchToken(value);
                                                            setShowCaptchError(null);
                                                        }}
                                                    />
                                                    {
                                                        (Object.keys(errors).length > 0) && (!recaptchaToken) ? (
                                                            <Text fontSize="12px" marginTop="4px" marginBottom="0px" color="#ff1e1e">Captcha is required</Text>
                                                        ) : showCaptchError ? (
                                                            <Text fontSize="12px" marginTop="4px" marginBottom="0px" color="#ff1e1e">{showCaptchError}</Text>
                                                        ) : ''
                                                    }
                                                </div>
                                            )
                                        }
                                        <Text className="pt-4 text-center">
                                            <span style={{ cursor: 'pointer', color: '#007bff' }} onClick={() => props.history.push('/forgotpassword')}>Forgot password?</span>
                                        </Text>
                                        <div>
                                            <SubmitButton
                                                type="submit"
                                                marginTop="4px"
                                                style={{ width: '292px' }}
                                            >SIGN IN <Img verticalAlign='middle' src={ArrowIcon} /></SubmitButton>
                                        </div>
                                        {showerrorMessage && <PaymentError header={errorMessage} message={null} />}
                                    </form>
                                </FlexContainer>
                            </div>
                        </FlexContainer>
                    )
                }
                {
                    showOTP && (
                        <FlexContainer
                            className="pt-3 pt-lg-5"
                            box-shadow="0 0 15px 0 rgba(0, 0, 0, 0.1)"
                            display="flow-root"
                        >
                            <OTP
                                isMobileView={false}
                                userEmail={otpDetail.userEmail}
                                firstHint={otpDetail.hint}
                                firstSession={otpDetail.session}
                                firstStatus={otpDetail.status}
                                setShowOTP={setShowOTP}
                                authSuccessCallback={otpDetail.authSuccessCallback}
                            />
                        </FlexContainer> 
                    )
                }
                <RecaptchaModal
                    visible={showRecaptcha}
                    onVerified={onGlobalRecaptchaVerified}
                />
            </>
        } />
    )
};

export default Login;
